import { useMemo } from 'react';
import DOMPurify from 'dompurify';

/**
 * SafeHtml — DOMPurify-sanitized HTML rendering component
 * ======================================================
 * Replaces dangerouslySetInnerHTML with a hardened, configurable sanitizer.
 *
 * Allowed tags: standard formatting + semantic HTML (no script, iframe, object, embed, form).
 * Allowed attributes: standard styling + data attributes (no on*, javascript:, etc.).
 *
 * Usage:
 *   <SafeHtml html={cmsContent} className="prose" />
 */

interface SafeHtmlProps {
  html: string;
  className?: string;
  tag?: 'div' | 'span' | 'p' | 'section' | 'article';
}

const ALLOWED_TAGS = [
  'p', 'br', 'hr', 'div', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'b', 'em', 'i', 'u', 's', 'del', 'ins', 'mark', 'small',
  'a', 'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'blockquote', 'q', 'cite', 'abbr', 'dfn', 'time',
  'sup', 'sub', 'kbd', 'code', 'pre', 'samp', 'var',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'caption',
  'img', 'figure', 'figcaption',
];

const ALLOWED_ATTR = [
  'href', 'title', 'alt', 'src', 'width', 'height',
  'class', 'id', 'style',
  'target', 'rel',
  'data-*',
  'aria-*',
  'role',
  'lang',
  'dir',
];

export default function SafeHtml({ html, className = '', tag: Tag = 'div' }: SafeHtmlProps) {
  const sanitized = useMemo(() => {
    if (!html || typeof html !== 'string') return '';
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      KEEP_CONTENT: true,
      // Force all links to open in a new tab with rel="noopener noreferrer"
      ADD_ATTR: ['target', 'rel'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout', 'onfocus', 'onblur'],
      FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'button', 'select'],
      // Strip out javascript: URLs entirely
      SANITIZE_DOM: true,
      // Remove any data URIs except for images
      ALLOW_DATA_ATTR: true,
    });
  }, [html]);

  if (!sanitized) return null;

  return (
    <Tag
      className={className}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
}